import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import { useAuth } from '../auth/useAuth';
import { DRIVE_SECTIONS } from './constants';
import type { DriveResource, DriveSectionId } from './types';
import { useDriveResources } from './hooks/useDriveResources';
import { DriveResourceList } from './components/DriveResourceList';
import { DriveResourceFormDialog, DriveResourceFormValues } from './components/DriveResourceFormDialog';
import { DeleteResourceDialog } from './components/DeleteResourceDialog';
import { extractGoogleDriveFileId } from './utils/googleDrive';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const initialSnackbarState: SnackbarState = {
  open: false,
  message: '',
  severity: 'info'
};

const Drive: React.FC = () => {
  const [activeSectionId, setActiveSectionId] = useState<DriveSectionId>('overview');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<DriveResource | null>(null);
  const [resourceToDelete, setResourceToDelete] = useState<DriveResource | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbarState, setSnackbarState] = useState<SnackbarState>(initialSnackbarState);

  const { user, userRole } = useAuth();
  const canCreateResource = userRole === 'ADMIN' || userRole === 'MANAGER';
  const canModerateResources = userRole === 'ADMIN';

  const { resources, loading, error, createResource, updateResource, deleteResource } = useDriveResources({
    section: activeSectionId,
    userId: user?.uid,
    enabled: !!user
  });

  const activeSection = useMemo(
    () => DRIVE_SECTIONS.find((section) => section.id === activeSectionId) ?? DRIVE_SECTIONS[0],
    [activeSectionId]
  );

  const closeSnackbar = () => setSnackbarState(initialSnackbarState);

  const showMessage = (message: string, severity: SnackbarState['severity'] = 'info') => {
    setSnackbarState({ open: true, message, severity });
  };

  const parseTags = (tags?: string) =>
    tags
      ?.split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0) || [];

  const parsePinWeight = (pinWeight?: string, fallback = 0) => {
    if (typeof pinWeight === 'undefined' || pinWeight === '') return fallback;
    const parsed = Number(pinWeight);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const handleCreateSubmit = async (values: DriveResourceFormValues, file: File | null) => {
    if (!user?.uid || !canCreateResource) {
      showMessage('You do not have permission to add Drive resources.', 'error');
      return;
    }
    try {
      setSubmitting(true);
      const trimmedExternalUrl = values.externalUrl?.trim();
      let googleDriveId: string | undefined;

      if (values.resourceType === 'google-drive') {
        googleDriveId = values.googleDriveId?.trim() || extractGoogleDriveFileId(trimmedExternalUrl);
        if (!googleDriveId) {
          showMessage('Unable to detect a Google Drive file ID. Please provide a valid sharing link or enter the ID manually.', 'error');
          return;
        }
      }

      await createResource({
        title: values.title,
        description: values.description?.trim() || undefined,
        section: activeSectionId,
        resourceType: values.resourceType,
        pinWeight: parsePinWeight(values.pinWeight, 0),
        tags: parseTags(values.tags),
        externalUrl: values.resourceType !== 'storage' ? trimmedExternalUrl : undefined,
        googleDriveId,
        file: values.resourceType === 'storage' ? file ?? undefined : undefined
      });
      setIsCreateDialogOpen(false);
      showMessage('Resource added to Drive', 'success');
    } catch (err) {
      console.error(err);
      if (err instanceof Error && err.message.includes('File selection')) {
        showMessage('Select a file to upload before saving the resource.', 'error');
      } else {
        showMessage('Failed to add resource. Please try again.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (values: DriveResourceFormValues, file: File | null) => {
    if (!editingResource || !canModerateResources) {
      showMessage('You do not have permission to update Drive resources.', 'error');
      return;
    }
    try {
      setSubmitting(true);
      const trimmedExternalUrl = values.externalUrl?.trim();
      let googleDriveId: string | undefined;

      if (editingResource.resourceType === 'google-drive') {
        googleDriveId =
          values.googleDriveId?.trim() ||
          extractGoogleDriveFileId(trimmedExternalUrl) ||
          editingResource.googleDriveId;
        if (!googleDriveId) {
          showMessage('Unable to detect a Google Drive file ID. Please provide a valid sharing link or enter the ID manually.', 'error');
          return;
        }
      }

      await updateResource({
        id: editingResource.id,
        title: values.title,
        description: values.description?.trim(),
        pinWeight: parsePinWeight(values.pinWeight, editingResource.pinWeight ?? 0),
        tags: parseTags(values.tags),
        externalUrl:
          editingResource.resourceType !== 'storage'
            ? trimmedExternalUrl || editingResource.externalUrl
            : undefined,
        googleDriveId,
        file: editingResource.resourceType === 'storage' ? file ?? undefined : undefined
      });
      setEditingResource(null);
      showMessage('Resource updated successfully', 'success');
    } catch (err) {
      console.error(err);
      showMessage('Failed to update resource. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!resourceToDelete || !canModerateResources) {
      showMessage('You do not have permission to delete Drive resources.', 'error');
      return;
    }
    try {
      setDeleting(true);
      await deleteResource(resourceToDelete.id);
      setResourceToDelete(null);
      showMessage('Resource deleted', 'success');
    } catch (err) {
      console.error(err);
      showMessage('Failed to delete resource. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditRequest = (resource: DriveResource) => {
    if (!canModerateResources) return;
    setEditingResource(resource);
  };

  const handleDeleteRequest = (resource: DriveResource) => {
    if (!canModerateResources) return;
    setResourceToDelete(resource);
  };

  // Calculate resource counts by type
  const resourceCounts = useMemo(() => {
    const counts = { storage: 0, googleDrive: 0, external: 0, total: 0 };
    resources.forEach(r => {
      if (r.resourceType === 'storage') counts.storage++;
      else if (r.resourceType === 'google-drive') counts.googleDrive++;
      else counts.external++;
      counts.total++;
    });
    return counts;
  }, [resources]);

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 } }}>
      <Stack spacing={3}>
        {/* Premium Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: (theme) => `linear-gradient(135deg,
              ${alpha(theme.palette.primary.main, 0.05)} 0%,
              ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)',
            }
          }}
        >
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box flex={1}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      borderRadius: 3,
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Drive
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Resource Repository
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: '800px' }}>
                  Important files and documents for when you need them.
                </Typography>

                {/* Quick Stats */}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label={`${resourceCounts.total} Total`}
                    size="small"
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label={`${resourceCounts.storage} Storage`}
                    size="small"
                    sx={{
                      bgcolor: (theme) => alpha('#2196f3', 0.1),
                      color: '#2196f3',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<FolderSharedIcon />}
                    label={`${resourceCounts.googleDrive} Drive`}
                    size="small"
                    sx={{
                      bgcolor: (theme) => alpha('#4caf50', 0.1),
                      color: '#4caf50',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    icon={<LinkIcon />}
                    label={`${resourceCounts.external} Links`}
                    size="small"
                    sx={{
                      bgcolor: (theme) => alpha('#9c27b0', 0.1),
                      color: '#9c27b0',
                      fontWeight: 600,
                    }}
                  />
                </Stack>
              </Box>
              {canCreateResource && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateDialogOpen(true)}
                  sx={{
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: '#fff',
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2.5,
                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                    textTransform: 'none',
                    '&:hover': {
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  New Resource
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeSectionId}
            onChange={(_, value) => setActiveSectionId(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              bgcolor: (theme) => alpha(activeSection.colorGradient.from, 0.02),
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: `linear-gradient(135deg, ${activeSection.colorGradient.from} 0%, ${activeSection.colorGradient.to} 100%)`,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 56,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: (theme) => alpha(activeSection.colorGradient.from, 0.08),
                }
              }
            }}
          >
            {DRIVE_SECTIONS.map((section) => {
              const SectionIcon = section.icon;
              const isActive = activeSectionId === section.id;
              return (
                <Tab
                  key={section.id}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: isActive
                            ? `linear-gradient(135deg, ${section.colorGradient.from} 0%, ${section.colorGradient.to} 100%)`
                            : 'transparent',
                          transition: 'all 0.2s',
                        }}
                      >
                        <SectionIcon
                          sx={{
                            fontSize: 18,
                            color: isActive ? '#fff' : 'text.secondary',
                          }}
                        />
                      </Box>
                      <span>{section.label}</span>
                    </Stack>
                  }
                  value={section.id}
                  sx={{
                    color: isActive ? section.colorGradient.from : 'text.secondary',
                  }}
                />
              );
            })}
          </Tabs>

          {/* Consolidated Section Header */}
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: 2.5,
              pb: 2,
              background: `linear-gradient(180deg, ${alpha(activeSection.colorGradient.from, 0.05)} 0%, transparent 100%)`,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {activeSection.blurb}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <DriveResourceList
              resources={resources}
              loading={loading}
              error={error}
              onEdit={handleEditRequest}
              onDelete={handleDeleteRequest}
              canManage={canModerateResources}
            />
          </Box>
        </Paper>
      </Stack>

      <DriveResourceFormDialog
        open={isCreateDialogOpen}
        mode="create"
        sectionId={activeSectionId}
        submitting={submitting}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
      />

      <DriveResourceFormDialog
        open={!!editingResource}
        mode="edit"
        sectionId={editingResource?.section || activeSectionId}
        resource={editingResource || undefined}
        submitting={submitting}
        onClose={() => setEditingResource(null)}
        onSubmit={handleEditSubmit}
      />

      <DeleteResourceDialog
        open={!!resourceToDelete}
        resource={resourceToDelete}
        deleting={deleting}
        onClose={() => setResourceToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbarState.severity} sx={{ width: '100%' }}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Drive;
